import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { projects, ENGAGEMENT_LABEL } from '../data/projects';
import ProjectArtBand from './ProjectArt';
import { cardSurface, Eyebrow } from './bandedStyles';

export { cardSurface, Eyebrow };

/**
 * Building blocks for the banded service-page layout (round 27, /services/managed-it).
 *
 * ServiceProse pours a whole argument into one white card. These pieces let a page
 * vary its shape instead: a section head, a card, a tile grid, a step list, chips,
 * town pills and a painted link card. They add no new visual language. Everything
 * is the site's own: Open Sans, the eight brand colours, a 20px card radius, 12px
 * controls and Midnight-tinted shadows.
 *
 * Plain markup throughout, so the text stays in the prerendered HTML exactly as
 * written. Nothing here animates.
 */

export const Block = styled.section`
  position: relative;
  z-index: 2;
`;

/* Headings state the point, so they run longer than a label: one step smaller on phones. */
export const SectionTitle = styled.h2`
  margin: 0 0 0.6rem;
  font-size: var(--fs-xl);
  font-weight: var(--font-weight-extra-bold);
  line-height: var(--lh-snug);
  letter-spacing: -0.01em;
  color: var(--davinci-midnight);

  &:last-child {
    margin-bottom: 0;
  }

  @media (min-width: 768px) {
    font-size: var(--fs-2xl);
  }
`;

const SectionLead = styled.p`
  margin: 0;
  font-size: var(--fs-base);
  line-height: var(--lh-normal);
  color: var(--text-primary);
`;

const HeadWrap = styled.div`
  max-width: 760px;
  margin-bottom: 18px;
`;

interface SectionHeadProps {
  id: string;
  title: string;
  eyebrow?: string;
  children?: React.ReactNode;
}

/** Eyebrow, H2 and an optional one-paragraph lead, left-aligned. */
export const SectionHead: React.FC<SectionHeadProps> = ({ id, title, eyebrow, children }) => (
  <HeadWrap>
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    <SectionTitle id={id}>{title}</SectionTitle>
    {children && <SectionLead>{children}</SectionLead>}
  </HeadWrap>
);

export const Card = styled.div`
  ${cardSurface}
  padding: 20px 18px 22px;
  color: var(--text-primary);

  @media (min-width: 768px) {
    padding: 26px 28px 28px;
  }

  a {
    color: var(--color-accent-primary-fill);
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

/**
 * A lighter container for secondary modules (co-managed, support day to day, where we work):
 * a hairline instead of a shadow, a quieter title, no eyebrow and no intro. The five buyer
 * questions get the full Card; everything that answers a side question gets this.
 */
export const QuietCard = styled.div`
  padding: 16px 16px 18px;
  border: 1px solid var(--davinci-cloud);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.55);
  color: var(--text-primary);

  @media (min-width: 768px) {
    padding: 20px 22px 22px;
  }

  a {
    color: var(--color-accent-primary-fill);
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

/** The title of a QuietCard; render it as an h2 or an h3 with the `as` prop. */
export const QuietTitle = styled.h2`
  margin: 0 0 0.45rem;
  font-size: var(--fs-lg);
  font-weight: var(--font-weight-extra-bold);
  line-height: var(--lh-snug);
  color: var(--davinci-midnight);
`;

/** The H3 that heads a card or a tab panel. */
export const CardTitle = styled.h3`
  margin: 0 0 0.5rem;
  font-size: var(--fs-lg);
  font-weight: var(--font-weight-extra-bold);
  line-height: var(--lh-snug);
  color: var(--davinci-midnight);
`;

export const Note = styled.p`
  margin: 0.7rem 0 0;
  font-size: var(--fs-sm);
  line-height: var(--lh-normal);
  color: var(--text-primary);

  a {
    color: var(--color-accent-primary-fill);
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

/** "Something else? Call ..." under the FAQ heading (the aside of the split FAQ). */
export const FaqCall = styled.p`
  margin: 14px 0 0;
  font-size: var(--fs-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);

  a {
    color: var(--color-accent-primary-fill);
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

/* ---------- chips ---------- */

export const ChipList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 14px 0 0;
  padding: 0;
  list-style: none;
`;

const ChipItem = styled.li`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
  padding: 0.35rem 0.8rem;
  border: 1px solid var(--davinci-cloud);
  border-radius: 999px;
  background: var(--davinci-steam);
  color: var(--davinci-midnight);
  font-size: var(--fs-sm);
  font-weight: var(--font-weight-semibold);
  line-height: 1.35;

  svg {
    flex: none;
    width: 15px;
    height: 15px;
    color: var(--davinci-blue-fill);
  }
`;

export const Chip: React.FC<{ icon?: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
  <ChipItem>
    {icon}
    {children}
  </ChipItem>
);

/* ---------- tiles: icon, title, one short line ---------- */

export const TileGrid = styled.ul`
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  margin: 18px 0 0;
  padding: 0;
  list-style: none;

  @media (min-width: 600px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  @media (min-width: 900px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 14px;
  }
`;

const TileItem = styled.li`
  display: grid;
  grid-template-columns: 34px 1fr;
  column-gap: 12px;
  align-items: start;
  margin: 0;
  padding: 10px 12px;
  border-radius: 14px;
  background: var(--davinci-steam);

  .icon {
    grid-row: 1 / 3;
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: var(--white);
    box-shadow: 0 1px 4px rgba(20, 53, 98, 0.12);
    color: var(--davinci-blue-fill);

    svg {
      width: 20px;
      height: 20px;
    }
  }

  strong {
    display: block;
    margin-bottom: 0.1rem;
    font-size: var(--fs-sm);
    line-height: 1.25;
    color: var(--davinci-midnight);
  }

  .line {
    display: block;
    font-size: var(--fs-sm);
    line-height: 1.45;
    color: var(--text-primary);
  }

  @media (min-width: 600px) {
    display: block;
    padding: 12px 12px 13px;

    .icon {
      margin-bottom: 0.55rem;
    }
  }

  @media (min-width: 900px) {
    padding: 16px;
  }
`;

export const Tile: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({
  icon,
  title,
  children,
}) => (
  <TileItem>
    <span className="icon" aria-hidden="true">{icon}</span>
    <strong>{title}</strong>
    <span className="line">{children}</span>
  </TileItem>
);

/* ---------- icon list: a short line per item, e.g. the tools in the "Our tools" tab ---------- */

export const IconList = styled.ul`
  display: grid;
  gap: 10px;
  margin: 14px 0 0;
  padding: 0;
  list-style: none;
`;

const IconRow = styled.li`
  display: grid;
  grid-template-columns: 34px 1fr;
  gap: 12px;
  margin: 0;
  font-size: var(--fs-sm);
  line-height: 1.5;
  color: var(--text-primary);

  .icon {
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: var(--davinci-steam);
    color: var(--davinci-blue-fill);

    svg {
      width: 20px;
      height: 20px;
    }
  }

  strong {
    color: var(--davinci-midnight);
  }
`;

export const IconItem: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
  <IconRow>
    <span className="icon" aria-hidden="true">{icon}</span>
    <div>{children}</div>
  </IconRow>
);

/** A figure picked out inside a sentence. Keep its text longer than the bare number, so the number is not a lone element. */
export const Stat = styled.span`
  display: inline-block;
  padding: 0 0.4rem;
  border-radius: 6px;
  background: rgba(11, 152, 206, 0.1);
  color: var(--davinci-midnight);
  font-weight: var(--font-weight-extra-bold);
`;

/* ---------- numbered steps joined by a vertical rule ---------- */

export const StepList = styled.ol`
  position: relative;
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: step;

  li {
    position: relative;
    margin: 0;
    padding: 0 0 12px 46px;
    counter-increment: step;
    font-size: var(--fs-sm);
    line-height: 1.45;
    color: var(--text-primary);
  }

  li:last-child {
    padding-bottom: 0;
  }

  li::before {
    content: counter(step);
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: var(--davinci-midnight);
    color: var(--white);
    font-weight: var(--font-weight-extra-bold);
    font-size: var(--fs-sm);
  }

  li::after {
    content: '';
    position: absolute;
    top: 34px;
    bottom: -2px;
    left: 16px;
    width: 2px;
    background: var(--davinci-cloud);
  }

  li:last-child::after {
    display: none;
  }

  strong {
    display: block;
    font-size: var(--fs-base);
    line-height: 1.3;
    color: var(--davinci-midnight);
  }
`;

/* ---------- "They keep / We add" ---------- */

export const KeepAdd = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin: 0.8rem 0 0.9rem;

  > div {
    padding: 12px;
    border-radius: 14px;
    font-size: var(--fs-sm);
    line-height: 1.45;
  }

  .keep {
    background: var(--davinci-steam);
    color: var(--text-primary);
  }

  .add {
    background: var(--davinci-midnight);
    color: var(--white);
  }

  b {
    display: block;
    margin-bottom: 0.3rem;
    font-size: max(13px, var(--fs-xs));
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--davinci-denim);
  }

  .add b {
    color: var(--davinci-beach);
  }
`;

/* ---------- contact channels, towns, remote row ---------- */

export const ChannelList = styled.ul`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin: 14px 0 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    margin: 0;
    padding: 0.6rem 0.7rem;
    border-radius: 12px;
    background: var(--davinci-steam);
    font-size: var(--fs-sm);
    font-weight: var(--font-weight-bold);
    line-height: 1.3;
    color: var(--davinci-midnight);
  }

  svg {
    flex: none;
    width: 20px;
    height: 20px;
    color: var(--davinci-blue-fill);
  }

  a {
    color: var(--davinci-midnight);
    text-decoration: none;
  }
`;

export const MiniLabel = styled.p`
  margin: 1rem 0 0;
  font-size: var(--fs-xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--davinci-denim);
`;

export const TownList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0.55rem 0 0;
  padding: 0;
  list-style: none;

  li {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    margin: 0;
    padding: 0.4rem 0.85rem;
    border-radius: 999px;
    background: var(--davinci-midnight);
    color: var(--white);
    font-size: var(--fs-sm);
    font-weight: var(--font-weight-bold);
    line-height: 1.3;
  }

  svg {
    width: 15px;
    height: 15px;
    color: var(--davinci-beach);
  }
`;

/** A soft blue callout with an icon: the after-hours line, the "Anywhere in Alberta" row. */
export const Callout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  margin-top: 0.9rem;
  padding: 12px 14px;
  border: 1px solid rgba(11, 152, 206, 0.18);
  border-radius: 14px;
  background: rgba(11, 152, 206, 0.08);
  font-size: var(--fs-sm);
  line-height: 1.5;
  color: var(--text-primary);

  > svg {
    flex: none;
    width: 20px;
    height: 20px;
    margin-top: 0.1rem;
    color: var(--davinci-blue-fill);
  }

  strong {
    color: var(--davinci-midnight);
  }

  &.tight {
    align-items: center;
    margin-top: 0.55rem;
    font-weight: var(--font-weight-bold);
    color: var(--davinci-midnight);
  }
`;

/* ---------- a painted card that links to one catalogue project ---------- */

const ArtCard = styled(Link)`
  display: block;
  overflow: hidden;
  border-radius: 16px;
  box-shadow: 0 6px 20px rgba(20, 53, 98, 0.08);
  text-decoration: none;
  transition: transform var(--transition-fast), box-shadow var(--transition-fast);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 26px rgba(20, 53, 98, 0.16);
  }

  &:focus-visible {
    outline: 3px solid var(--davinci-blue-fill);
    outline-offset: 2px;
  }
`;

const ArtCaption = styled.span`
  display: block;
  padding: 10px 12px;
  background: var(--davinci-midnight);
  color: var(--white);
  font-size: var(--fs-sm);
  font-weight: var(--font-weight-semibold);
  line-height: 1.35;

  em {
    display: block;
    font-style: normal;
    font-size: var(--fs-xs);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--davinci-beach);
  }
`;

/**
 * A painting from the project catalogue with the project's own title under it.
 * The title comes from the catalogue, so it cannot drift; an unknown slug
 * renders nothing. In-house platforms carry the catalogue's badge text and are
 * never presented as client work.
 */
export const ProjectArtLink: React.FC<{ slug: string; className?: string }> = ({ slug, className }) => {
  const project = projects.find((p) => p.slug === slug);
  if (!project) return null;
  return (
    <ArtCard to={`/projects/${project.slug}`} className={className}>
      <ProjectArtBand slug={project.slug} variant="card" />
      <ArtCaption>
        <em>{project.engagement === 'in-house' ? ENGAGEMENT_LABEL['in-house'] : 'Related project'}</em>
        {project.title} &rarr;
      </ArtCaption>
    </ArtCard>
  );
};

/* ---------- ticket flow: a real-data strip, one cell per stage ---------- */

const FlowList = styled.ol`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
  counter-reset: stage;

  li {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin: 0;
    padding: 10px 12px 11px;
    border-radius: 14px;
    background: var(--davinci-steam);
    counter-increment: stage;
  }

  .stage {
    font-size: max(13px, var(--fs-xs));
    font-weight: var(--font-weight-bold);
    line-height: 1.3;
    color: var(--davinci-denim);
  }

  .stage::before {
    content: counter(stage) '. ';
  }

  .fig {
    font-size: var(--fs-lg);
    font-weight: var(--font-weight-extra-bold);
    line-height: 1.2;
    color: var(--davinci-midnight);
  }

  .fig a {
    color: var(--davinci-midnight);
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  .fig a:focus-visible {
    outline: 3px solid var(--davinci-blue-fill);
    outline-offset: 2px;
    border-radius: 4px;
  }

  .cap {
    font-size: max(13px, var(--fs-xs));
    line-height: 1.35;
    color: var(--text-primary);
  }
`;

export interface TicketFlowStep {
  /** The stage, two or three words. */
  stage: string;
  /** The headline figure, or a link; a proofPoints.ts value, never typed in. */
  figure: React.ReactNode;
  /** What the figure measures, with its period. */
  caption: string;
}

/**
 * How a client request moves through the service desk, as four cells. Every figure comes from
 * proofPoints.ts with its period in the caption, so the strip cannot drift from the proof band
 * or claim more than it measures. It shows no ticket text and no client names.
 */
export const TicketFlow: React.FC<{ steps: TicketFlowStep[]; label: string }> = ({ steps, label }) => (
  <FlowList aria-label={label}>
    {steps.map((step) => (
      <li key={step.stage}>
        <span className="stage">{step.stage}</span>
        <span className="fig">{step.figure}</span>
        <span className="cap">{step.caption}</span>
      </li>
    ))}
  </FlowList>
);
